import React from 'react';

import styled from 'styled-components';

import { Calories } from 'components/price-and-calories';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useMenuContext } from 'state/menu';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import { MOBILE_TILE_BACKGROUND_COLOR } from './constants';
import {
  ItemPrice,
  ListTileImage,
  PriceAndCaloriesListWrapper,
  TileImage,
  TileInner,
  tileHeaderStyles,
} from './styled';
import { IMenuTileProps } from './types';

const ItemCalories = styled.span`
  margin-block-end: 0;
`;

const TileDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: ellipsis;
  padding: 0.5rem;
  text-align: center;

  ${Styles.desktop} {
    padding: 1rem 2rem;
    text-align: unset;
  }

  p {
    color: ${Styles.color.grey.two};
    font-family: ${Styles.fontFamily.body};
    margin-block-start: 0;
  }
`;

const TileListDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: ellipsis;

  ${Styles.mobile} {
    width: calc(100% - 6.725rem);
    text-align: start;
    padding-inline-start: 1rem;
  }

  ${Styles.desktop} {
    padding: 1rem 2rem;
  }

  p {
    color: ${Styles.color.grey.two};
    font-family: ${Styles.fontFamily.body};
  }
`;

const TileHeader = styled.span`
  ${tileHeaderStyles}
`;

export const TileWrapper = styled.div`
  background: ${MOBILE_TILE_BACKGROUND_COLOR};
  border-radius: ${Styles.borderRadius};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  padding: 0;

  ${Styles.desktop} {
    height: 100%;
    min-height: 20rem;
    background: ${Styles.color.white};
  }
`;

const StyledTileInner = styled(TileInner)`
  display: flex;
  align-items: center;
  padding: 0.5rem;

  ${Styles.desktop} {
    padding: 0;
    display: block;
  }
`;

const StyledListItemCalories = styled(ItemCalories)`
  margin-block-start: 0;
  color: ${Styles.color.black};
`;

const ItemCaloriesGridWrapper = styled(ItemCalories)`
  color: ${Styles.color.grey.two};
`;

const StyledItemPrice = styled(ItemPrice)`
  color: ${Styles.color.black};
  font-weight: normal;
`;

const SeparatorStyle = styled.span`
  color: ${Styles.color.black};
  margin: 0 0.3rem;
`;

const StyledPriceAndCaloriesListWrapper = styled(PriceAndCaloriesListWrapper)`
  margin-block-start: 0.3rem;

  ${Styles.desktop} {
    display: flex;
  }
`;

const PriceAndCaloriesWrapper = styled(PriceAndCaloriesListWrapper)`
  margin-block-start: 0.3rem;
  flex-direction: column;

  ${Styles.desktop} {
    display: flex;
    flex-direction: row;
  }
`;

export const Tile = React.memo(
  ({
    enableMenuList,
    image,
    imageDescription,
    name,
    type,
    price,
    calories,
    id,
  }: IMenuTileProps) => {
    const enablePriceAndCaloriesInMenu = useFlag(
      LaunchDarklyFlag.ENABLE_PRICE_AND_CALORIES_IN_MENU
    );
    const {
      location: { pathname },
    } = useLocationContext();
    const { showStaticMenu } = useMenuContext();
    const shouldRenderPriceAndCalories =
      enablePriceAndCaloriesInMenu &&
      type !== 'section' &&
      ((!showStaticMenu && price) || calories);

    const [PrinceAndCalories, ItemCaloriesWrapper] = enableMenuList
      ? [StyledPriceAndCaloriesListWrapper, StyledListItemCalories]
      : [PriceAndCaloriesWrapper, ItemCaloriesGridWrapper];

    const tileTestId = `tile-header-${(name || '').toLowerCase().replace(/\s/g, '')}`;

    const tileContent = (
      <>
        <TileHeader data-testid={tileTestId}>{name}</TileHeader>
        {shouldRenderPriceAndCalories && (
          <PrinceAndCalories>
            {!showStaticMenu && (
              <>
                <StyledItemPrice>{price}</StyledItemPrice>
                <SeparatorStyle role="separator">•</SeparatorStyle>
              </>
            )}
            <Calories>
              <ItemCaloriesWrapper>{calories}</ItemCaloriesWrapper>
            </Calories>
          </PrinceAndCalories>
        )}
      </>
    );

    const [Inner, Image, Description] = enableMenuList
      ? [StyledTileInner, ListTileImage, TileListDescription]
      : [TileInner, TileImage, TileDescription];

    const handleOnImageLoaded = () => {
      emitEvent(EventName.MENU_TILE_IMAGE_LOADED, {
        sanityId: id,
        isOnMainMenu: pathname === routes.menu,
        attributes: [],
      });
    };

    return (
      <TileWrapper>
        <Inner>
          <Image image={image} alt={imageDescription ?? ''} onImageLoaded={handleOnImageLoaded} />
          <Description>{tileContent}</Description>
        </Inner>
      </TileWrapper>
    );
  }
);
